import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Tag, 
  Space, 
  Row, 
  Col,
  DatePicker,
  message,
  Typography,
  Tooltip,
  Statistic,
  Modal, Checkbox, Form
} from 'antd';
import { 
  EyeOutlined, 
  FileTextOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { getExternalVoucherExpiryDate } from '../../utils/externalVoucherExpiry';
import { formatMoney } from '../../utils/formatMoney';
import {
  buildOrderCardSerialNumber,
  getPrimaryOrderSerialNumber,
  resolveOrderSerialBase,
} from '../../utils/orderSerialNumber';

const { Text } = Typography;

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dateRange: null
  });
  const [dateRange, setDateRange] = useState(null);
  const [orderStats, setOrderStats] = useState({
    totalOrders: 0,
    totalAmount: 0,
    dateRangeText: 'All time'
  });

  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportOrder, setExportOrder] = useState(null);
  const [exportSelectedKeys, setExportSelectedKeys] = useState([]);
  const [exportSelectedFields, setExportSelectedFields] = useState([
    'Order Number', 'Voucher Name', 'Brand', 'Code', 'Serial Number', 'PIN', 'Status', 'Expiry Date', 'Issued At'
  ]);
  const [exportLoading, setExportLoading] = useState(false);
  const exportFields = [
    { label: 'Order Number', value: 'Order Number' },
    { label: 'Voucher Name', value: 'Voucher Name' },
    { label: 'Brand', value: 'Brand' },
    { label: 'Code', value: 'Code' },
    { label: 'Serial Number', value: 'Serial Number' },
    { label: 'PIN', value: 'PIN' },
    { label: 'Status', value: 'Status' },
    { label: 'Expiry Date', value: 'Expiry Date' },
    { label: 'Issued At', value: 'Issued At' }
  ];

  const processVoucherCodesData = (order) => {
    if (!order) return [];
    const voucherCodesData = [];
    if (order.voucherInstances && order.voucherInstances.length > 0) {
      const groupedInstances = {};
      order.voucherInstances.forEach(instance => {
        const voucherId = instance.voucherDetails?.voucherId || instance.voucherDetails?._id || instance.voucherId || instance.voucherDetails?.id || instance.voucherDetails?.name || 'unknown';
        const voucherName = instance.voucherDetails?.voucherName || instance.voucherDetails?.name || instance.voucherName || 'Unknown Voucher';
        const voucherBrand = instance.voucherDetails?.voucherBrand || instance.voucherBrand || 'Unknown Brand';
        const groupKey = `${voucherId}__${voucherName}__${voucherBrand}`;
        if (!groupedInstances[groupKey]) {
          groupedInstances[groupKey] = {
            voucherId,
            voucherName,
            voucherBrand,
            codes: []
          };
        }
        groupedInstances[groupKey].codes.push({
          code: instance.voucherCode,
          serialNumber: instance.voucherCodeId?.serialNumber || instance.serialNumber,
          pin: instance.voucherCodeId?.pin || instance.pin,
          expiryDate: instance.voucherCodeId?.expiryDate || instance.expiryDate,
          status: instance.status,
          issuedAt: instance.issuedAt
        });
      });
      Object.values(groupedInstances).forEach(group => {
        voucherCodesData.push(group);
      });
    }

    // Fallback for external provider orders: read cards directly from externalOrderData
    if (voucherCodesData.length === 0 && order.isExternalVoucherOrder) {
      const firstItem = Array.isArray(order.items) ? order.items[0] : null;
      const voucherId =
        firstItem?.voucherId ||
        firstItem?.externalVoucherData?.externalId ||
        'external';
      const voucherName = firstItem?.externalVoucherData?.name || 'External Voucher';
      const voucherBrand =
        firstItem?.externalVoucherData?.brand ||
        order?.externalOrderData?.type ||
        'External';

      const fallbackCodes = [];
      const externalOrderNo = resolveOrderSerialBase(
        order?.externalOrderData?.orderNo,
        order?.externalOrderData?.order_id,
        order?.externalOrderData?.serialNumber,
      );

      if (Array.isArray(order?.externalOrderData?.cards) && order.externalOrderData.cards.length > 0) {
        order.externalOrderData.cards.forEach((card, index) => {
          const code = card?.code || card?.cards || card?.number;
          if (!code) return;
          fallbackCodes.push({
            code,
            serialNumber: buildOrderCardSerialNumber(externalOrderNo, index),
            pin: card?.pin || card?.password || null,
            expiryDate: getExternalVoucherExpiryDate({
              expiryDate: card?.expiryDate,
              deadline: card?.deadline,
            }),
            status: 'issued',
            issuedAt: order?.timestamps?.processedAt || order?.createdAt,
          });
        });
      } else {
        const singleCode = order?.externalOrderData?.secret_code || order?.externalOrderData?.egift_exchangecode;
        if (singleCode) {
          fallbackCodes.push({
            code: singleCode,
            serialNumber:
              order?.externalOrderData?.serialNumber ||
              getPrimaryOrderSerialNumber(externalOrderNo),
            pin: null,
            expiryDate: getExternalVoucherExpiryDate({
              availableEnd: order?.externalOrderData?.available_end,
            }),
            status: 'issued',
            issuedAt: order?.timestamps?.processedAt || order?.createdAt,
          });
        }
      }

      if (fallbackCodes.length > 0) {
        voucherCodesData.push({
          voucherId: String(voucherId),
          voucherName,
          voucherBrand,
          codes: fallbackCodes,
        });
      }
    }
    return voucherCodesData;
  };

  const exportSelectedVoucherCodesToExcel = () => {
    if (!exportOrder) return;
    const voucherCodesData = processVoucherCodesData(exportOrder);
    let exportGroups = voucherCodesData;
    if (exportSelectedKeys.length > 0) {
      exportGroups = voucherCodesData.filter((_, idx) => exportSelectedKeys.includes(idx.toString()));
    }
    // Create workbook
    const workbook = XLSX.utils.book_new();
    
    // Track used worksheet names for uniqueness
    const usedSheetNames = new Set();
    
    // Create sheet for each voucher type
    exportGroups.forEach((voucherData, index) => {
      if (!voucherData?.codes?.length) return;
      // Determine column names by voucher type
      const hasSerialNumber = voucherData.codes.some(code => code.serialNumber);
      const hasPIN = voucherData.codes.some(code => code.pin);
      const hasExpiryDate = voucherData.codes.some(code => code.expiryDate);
      const headers = [
        'Order Number',
        'Voucher Name', 
        'Brand',
        'Code',
        ...(hasSerialNumber ? ['Serial Number'] : []),
        ...(hasPIN ? ['PIN'] : []),
        'Status',
        ...(hasExpiryDate ? ['Expiry Date'] : []),
        'Issued At'
      ].filter(h => exportSelectedFields.includes(h));
      const rows = voucherData.codes.map(code => [
        exportOrder?.orderNumber || '',
        voucherData.voucherName || '',
        voucherData.voucherBrand || '',
        code.code || '',
        ...(hasSerialNumber ? [code.serialNumber || ''] : []),
        ...(hasPIN ? [code.pin || ''] : []),
        code.status || '',
        ...(hasExpiryDate ? [code.expiryDate ? dayjs(code.expiryDate).format('YYYY-MM-DD') : ''] : []),
        code.issuedAt ? dayjs(code.issuedAt).format('YYYY-MM-DD HH:mm:ss') : ''
      ].filter((_, idx) => headers[idx]));
      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      // Set column width
      const colWidths = headers.map(header => ({ wch: Math.max(header.length, 15) }));
      worksheet['!cols'] = colWidths;
      
      // Generate unique worksheet name
      let baseSheetName = voucherData.voucherName || `Voucher_${index + 1}`;
      // Clean worksheet name, remove Excel-unsupported chars
      baseSheetName = baseSheetName.replace(/[\\/?*[\]:]/g, '_');
      // Limit sheet name length (Excel max 31 chars)
      if (baseSheetName.length > 31) {
        baseSheetName = baseSheetName.substring(0, 28) + '...';
      }
      
      let sheetName = baseSheetName;
      let counter = 1;
      
      // Ensure worksheet name uniqueness
      while (usedSheetNames.has(sheetName)) {
        const suffix = `_${counter}`;
        const maxLength = 31 - suffix.length;
        const truncatedBase = baseSheetName.length > maxLength ? 
          baseSheetName.substring(0, maxLength) : baseSheetName;
        sheetName = `${truncatedBase}${suffix}`;
        counter++;
      }
      
      usedSheetNames.add(sheetName);
      
      // Add sheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });
    // Export file
    const fileName = `${exportOrder?.orderNumber || 'order'}_voucher_codes.xlsx`;
    XLSX.writeFile(workbook, fileName);
    setExportModalVisible(false);
  };

  // Validate at least one voucher type selected
  const handleExportOk = () => {
    if (!exportSelectedKeys || exportSelectedKeys.length === 0) {
      message.warning('Please select at least one voucher type to export.');
      return;
    }
    exportSelectedVoucherCodesToExcel();
  };

  const fetchOrders = async (page = 1, pageSize = 10, searchFilters = {}) => {
    try {
      setLoading(true);
      
      const params = {
        page,
        limit: pageSize,
        search: searchFilters.search || filters.search,
        status: searchFilters.status || filters.status
      };
      
      if (searchFilters.dateRange || filters.dateRange) {
        const dateRange = searchFilters.dateRange || filters.dateRange;
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      
      Object.keys(params).forEach(key => 
        (params[key] === '' || params[key] === null || params[key] === undefined) && delete params[key]
      );
      
      const response = await axios.get('/orders', { params });
      const responseData = response.data || {};
      const responsePagination = responseData.pagination || {};
      
      setOrders(Array.isArray(responseData.orders) ? responseData.orders : []);
      setPagination({
        current: responsePagination.currentPage || responseData.currentPage || page,
        pageSize: responsePagination.limit || responseData.limit || pageSize,
        total: responsePagination.totalCount || responseData.totalCount || 0
      });
      
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTableChange = (paginationConfig, filters, sorter) => {
    fetchOrders(paginationConfig.current, paginationConfig.pageSize);
  };

  const getStatusColor = (status) => {
    if (status === 'completed') return 'green';
    if (status === 'pending') return 'orange';
    if (status === 'failed') return 'red';
    return 'default';
  };
  const getStatusLabel = (status) => {
    if (status === 'completed') return '✅ Completed';
    if (status === 'pending') return '⏳ Pending';
    if (status === 'failed') return '❌ Failed';
    return status;
  };

  const getStatusDescription = (status, record) => {
    switch (status) {
      // Simplified status system
      case 'pending':
        if (record?.failureReason && record?.isExternalVoucherOrder) {
          return record.failureReason;
        }
        return 'Order is being processed and vouchers are being prepared';
      case 'completed':
        return 'Order complete! Your voucher codes are ready to download and use';
      case 'failed': {
        const fr = record?.failureReason;
        if (fr && String(fr).trim()) return String(fr).trim();
        const desc = record?.statusInfo?.description;
        if (desc && String(desc).trim() && !/^Order failed:\s*Unknown error$/i.test(String(desc).trim())) {
          return String(desc).replace(/^Order failed:\s*/i, '').trim() || desc;
        }
        return 'Order could not be completed. Please contact support for assistance';
      }
      
      // Legacy compatibility
      case 'confirmed':
      case 'processing':
        return 'Order is being processed and vouchers are being prepared';
      case 'shipped':
      case 'delivered':
        return 'Order complete! Your voucher codes are ready to download and use';
      case 'insufficient_balance':
        return 'Order failed due to insufficient account balance';
      case 'out_of_stock':
        return 'Order failed due to insufficient stock';
      case 'cancelled':
        return 'This order was cancelled';
      case 'refunded':
        return 'Order refunded, check your account balance';
      default:
        return 'Click to view order details';
    }
  };

  // The status column used to be hidden whenever every order on the page
  // shared one status, which left a page of pending orders with no status
  // shown at all. Always show it.
  const showStatusColumn = true;

  // Calculate order stats
  const calculateOrderStats = (filteredOrders, dateRange) => {
    const totalOrders = filteredOrders.length;
    const totalAmount = filteredOrders.reduce((sum, order) => {
      // Only sum numeric amounts; order.accountDeduction is an object
      // ({ fromTopUp, fromCredit, ... }), not a scalar — using it directly
      // would NaN out the running total.
      const candidates = [
        order.totalAmount,
        order.amount,
        typeof order.accountDeduction === 'object'
          ? Number(order.accountDeduction?.fromTopUp || 0) +
            Number(order.accountDeduction?.fromCredit || 0)
          : Number(order.accountDeduction || 0),
      ];
      const amount = candidates.find((v) => Number.isFinite(Number(v))) || 0;
      return sum + Number(amount);
    }, 0);
    
    let dateRangeText = 'All time';
    if (dateRange && dateRange[0] && dateRange[1]) {
      dateRangeText = `${dayjs(dateRange[0]).format('MMM DD, YYYY')} ~ ${dayjs(dateRange[1]).format('MMM DD, YYYY')}`;
    }
    
    return { totalOrders, totalAmount, dateRangeText };
  };

  // The server already applies the date filter via fetchOrders, so callers
  // get the current page rows back unchanged. We keep the helper so the
  // existing call sites continue to work.
  const getFilteredOrders = () => [...orders];

  // Handle date filter change. Date filtering must round-trip through the
  // server because the table is server-paginated — filtering only the
  // currently loaded page would silently hide matching orders that live on
  // other pages and would also produce misleading totals.
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setFilters((prev) => ({ ...prev, dateRange: dates || null }));
    fetchOrders(1, pagination.pageSize, { dateRange: dates || null });
    // Render-time stats below will refresh once `orders` updates from the
    // server response; show a sensible label in the meantime.
    setOrderStats((prev) => ({
      ...prev,
      dateRangeText:
        dates && dates[0] && dates[1]
          ? `${dayjs(dates[0]).format('MMM DD, YYYY')} ~ ${dayjs(dates[1]).format('MMM DD, YYYY')}`
          : 'All time',
    }));
  };

  // Initialize stats
  useEffect(() => {
    if (orders.length > 0) {
      const filteredOrders = getFilteredOrders();
      const stats = calculateOrderStats(filteredOrders, dateRange);
      setOrderStats(stats);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, dateRange]);

  // 2. Dynamically generate columns
  const columns = [
    {
      title: 'Order Number',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (orderNumber, record) => (
        <div>
          <Text
            copyable={{ text: orderNumber }}
            style={{ color: '#1890ff', fontSize: 15, cursor: 'pointer', userSelect: 'text' }}
            onClick={() => navigate(`/client/orders/${record._id}`)}
          >
            {orderNumber}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {dayjs(record.createdAt).format('YYYY-MM-DD')}
          </Text>
        </div>
      ),
    },
    {
      title: 'Items',
      dataIndex: 'items',
      key: 'items',
      render: (items) => {
        const itemCount = Array.isArray(items) ? items.length : 0;
        const voucherCount = Array.isArray(items) ? items.reduce((sum, item) => sum + (item && item.quantity ? item.quantity : 0), 0) : 0;

        const detail = Array.isArray(items) && items.length > 0
          ? items.map(item => {
              let voucherName = 'Unknown Voucher';
              let voucherBrand = '';

              // Handle internal vouchers (populated voucherId)
              if (item.voucherId && typeof item.voucherId === 'object' && item.voucherId.name) {
                voucherName = item.voucherId.name;
                voucherBrand = item.voucherId.brand || '';
              }
              // Handle external vouchers
              else if (item.externalVoucherData && item.externalVoucherData.name) {
                voucherName = item.externalVoucherData.name;
                voucherBrand = item.externalVoucherData.brand || '';
              }

              const displayName = voucherBrand ? `${voucherBrand} - ${voucherName}` : voucherName;
              return `${displayName} x${item.quantity || 0}`;
            }).join(', ')
          : 'No items';

        return (
          <Tooltip title={detail} placement="top">
            <div>
              <Text style={{ fontSize: 15 }}>{itemCount} item(s)</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>{voucherCount} vouchers</Text>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      align: 'right',
      render: (amount, record) => (
        <div style={{ textAlign: 'right' }}>
          <Text strong style={{ fontSize: '14px' }}>{formatMoney(amount, record.currency)}</Text>
        </div>
      ),
    },
    // Dynamically insert status column
    ...(showStatusColumn ? [{
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <div>
          <Tag color={getStatusColor(status)} style={{ marginBottom: 4 }}>
            {getStatusLabel(status)}
          </Tag>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {getStatusDescription(status, record)}
          </Text>
        </div>
      ),
    }] : []),
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Order Details">
            <Button 
              type="primary" 
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/client/orders/${record._id}`)}
            />
          </Tooltip>
          <Tooltip title="Export Codes">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              type="default"
              loading={exportLoading && exportOrder && exportOrder._id === record._id}
              onClick={async () => {
                setExportLoading(true);
                try {
                  const res = await axios.get(`/orders/${record._id}`);
                  setExportOrder(res.data.order);
                  // Default select all voucher types
                  const voucherCodesData = processVoucherCodesData(res.data.order);
                  setExportSelectedKeys(voucherCodesData.map((_, idx) => idx.toString()));
                  setExportSelectedFields(exportFields.map(f => f.value));
                  setExportModalVisible(true);
                } catch (e) {
                  message.error('Failed to load order details');
                } finally {
                  setExportLoading(false);
                }
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      {/* Page header style matches Voucher Catalog */}
      <div style={{ marginBottom: 24 }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: '#222', letterSpacing: 1 }}>My Orders</span>
      </div>
      
      {/* Filter area: white div, left align content */}
      {/* Date Range filter area */}
      <div className="responsive-filter-card" style={{ marginBottom: 16, background: '#fff', padding: '16px 0 8px 0', boxShadow: 'none', border: 'none', borderRadius: 0, width: '100%' }}>
        <Row className="responsive-filter-row" gutter={[12, 12]} align="middle" style={{ marginLeft: 0, marginRight: 0, maxWidth: 520 }}>
          <Col xs={24} sm={16}>
            <div className="responsive-filter-field" data-filter-label="Date Range">
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                value={dateRange}
                onChange={handleDateRangeChange}
                placeholder={['Start Date', 'End Date']}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="responsive-filter-actions" style={{ paddingTop: 18 }}>
              <Button
                size="small"
                style={{ height: 32 }}
                onClick={() => {
                  setDateRange(null);
                  setOrderStats(calculateOrderStats(getFilteredOrders(null), null));
                  // Refetch from server so the table reflects "all time" too —
                  // previously only local stats were reset and the list still
                  // showed orders filtered by the prior date range.
                  fetchOrders(1, pagination.pageSize, { dateRange: null });
                }}
              >
                Reset
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {/* Order stats area */}
      {/* Stats area All time style */}
      <Card 
        style={{ 
          marginBottom: 16, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          border: '1px solid #e2e8f0',
          borderRadius: 12
        }}
        bodyStyle={{ padding: '16px 20px' }}
      >
        <Row gutter={24} align="middle">
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'left', marginLeft: 8 }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 4, fontWeight: 500 }}>
                {orderStats.dateRangeText}
              </div>
            </div>
          </Col>
          <Col xs={12} sm={8}>
            <Statistic
              title="Total Orders"
              value={orderStats.totalOrders}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: 20 }}
            />
          </Col>
          <Col xs={12} sm={8}>
            <Statistic
              title="Total Amount (this page)"
              value={formatMoney(orderStats.totalAmount, orders[0]?.currency || 'USD')}
              valueStyle={{ color: '#52c41a', fontSize: 20 }}
            />
          </Col>
        </Row>
      </Card>

      {/* Table area: render Table directly, keep style */}
      <Table
        columns={columns}
        dataSource={getFilteredOrders()}
        rowKey="_id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
        }}
        onChange={handleTableChange}
      />

      {/* Modal section */}
      <Modal
        title="Export Codes"
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        onOk={handleExportOk}
        okText="Export"
        cancelText="Cancel"
      >
        <Form layout="vertical">
          <Form.Item label="Select voucher types to export">
            <Checkbox.Group
              style={{ width: '100%' }}
              value={exportSelectedKeys}
              onChange={setExportSelectedKeys}
            >
              <Row gutter={[0, 8]}>
                {processVoucherCodesData(exportOrder).map((voucher, idx) => (
                  <Col span={24} key={idx}>
                    <Checkbox value={idx.toString()}>
                      {voucher.voucherName} ({voucher.codes.length} codes)
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item label="Select fields to export">
            <Checkbox.Group
              style={{ width: '100%' }}
              value={exportSelectedFields}
              onChange={setExportSelectedFields}
            >
              <Row gutter={[0, 8]}>
                {exportFields.map(field => (
                  <Col span={12} key={field.value}>
                    <Checkbox value={field.value}>{field.label}</Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Orders;
